import { useMemo } from 'react'

import { DateTime } from 'luxon'

import { digObject } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { OrderItemModel, OrderItemRequestOptions } from '@models/types'

import useOrderItems from '@hooks/useOrderItems'
import useSelector from '@hooks/useSelector'
import { EntitiesState } from '@redux/entities'

import { sortByPriorityCampaigns } from '@functions/priorityCampaign'

const watchEntityKeys = ['campaigns', 'orderItems', 'services', 'statuses']

type GroupedOrderItems = {
  [key: string]: {
    key: string,
    orderItems: OrderItemModel[],
    title: string,
  },
};

type SortByPriorityOrderItemsParams = {
  groupedItems: GroupedOrderItems,
  priorityOnly: boolean,
  selectedPriorityUserId: number,
}

const sortByPriorityOrderItems = (params: SortByPriorityOrderItemsParams) => {
  const { groupedItems, priorityOnly, selectedPriorityUserId } = params

  const sortedFilteredItems = Object.values(groupedItems).map((group) => {
    const filteredOrderItems = group.orderItems
      .filter((orderItem) => {
      // If priorityOnly is true, filter out order items
        if (priorityOnly && !orderItem.priority_campaign_user_ids?.includes(selectedPriorityUserId)){
          return false
        }

        return true
      })

    // @ts-expect-error TS2322
    group.orderItems = sortByPriorityCampaigns({ entities: filteredOrderItems, selectedPriorityUserId })

    return group
  })

  return sortedFilteredItems
}

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    signboardApproved: `#/${prefixRoute}/overview/signboards?status=approved`,
    signboardPending: `#/${prefixRoute}/overview/signboards?status=pending`,
    signboardsAll: `#/${prefixRoute}/overview/signboards`,
    signboardsInstalled: `#/${prefixRoute}/overview/signboards?status=installed`,
    signboardsProcessing: `#/${prefixRoute}/overview/signboards?status=processing`,
    signboardsRemoved: `#/${prefixRoute}/overview/signboards?status=removed`,
  }
}

interface GetGroupKeyParams {
  lastCompletedTaskId?: number,
  orderItem: OrderItemModel,
  orderItemTasks: EntitiesState['orderItemTasks'],
}

const getGroupKey = (params: GetGroupKeyParams) => {
  const { lastCompletedTaskId, orderItem, orderItemTasks } = params

  const orderItemTask = lastCompletedTaskId && orderItemTasks[lastCompletedTaskId]

  // @ts-expect-error TS2345
  const taskCompletedDate = digObject(orderItemTask, 'completed_date.date', orderItem.updated_at)

  const completedDate = DateTime.fromFormat(taskCompletedDate, 'dd/MM/yyyy')

  const sevenDaysAgo = DateTime.now().minus({ days: 7 })
  const threeMonthsAgo = DateTime.now().minus({ months: 3 })

  // last Seven Days
  if (completedDate >= sevenDaysAgo) return 'lastSevenDays'

  // within Three Months
  if (completedDate <= sevenDaysAgo && completedDate > threeMonthsAgo) return 'withinThreeMonths'

  // over 3 Months
  if (completedDate <= threeMonthsAgo) return 'overThreeMonths'

  return 'noDateSet'
}

const getGroupedOrderItems = (orderItems: OrderItemModel[], orderItemTasks: EntitiesState['orderItemTasks']) => {
  const groupedOrderItems = {
    lastSevenDays: {
      key: 'last_seven_days',
      orderItems: [],
      title: 'Last Seven Days',
    },
    withinThreeMonths: {
      key: 'within_three_months',
      orderItems: [],
      title: 'Within Three Months',
    },
    overThreeMonths: {
      key: 'over_three_months',
      orderItems: [],
      title: 'Over 3 Months',
    },
    noDateSet: {
      key: 'no_date_set',
      orderItems: [],
      title: 'No Date Set',
    },
  }

  orderItems.map((item) => {
    const { last_completed_task_id } = item

    const groupKey = getGroupKey({ lastCompletedTaskId: last_completed_task_id, orderItem: item, orderItemTasks })

    // @ts-expect-error TS2345
    return groupedOrderItems[groupKey].orderItems.push(item)
  })

  return groupedOrderItems
}

type OrderItemsFilters = {
  agency_id?: number,
  agent_id?: number,
  campaign_id?: number,
  campaignIsHidden?: boolean,
  exclude_without_campaign?: boolean,
  order_id?: number,
  order_by?: string,
  priority_order_items_by_user_id?: number,
  sort_by_priority_campaign_for_user_id?: number,
  status?: string,
  taskable_only?: boolean,
}

type UseOrderItemsOptions = {
  filters?: OrderItemsFilters,
  performHttpRequests?: boolean,
  requestOptions?: OrderItemRequestOptions,
}

function useSignboards(options: UseOrderItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    sort_by_priority_campaign_for_user_id: selectedPriorityUserId,
    priority_order_items_by_user_id: priorityForUserId,
  } = filters || {}

  const priorityUserId = priorityForUserId || selectedPriorityUserId

  const {
    updatedEntities: {
      campaigns: campaignsUpdatedAt,
      orderItems: orderItemsUpdatedAt,
      services: servicesUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const orderItemTasks = useSelector(reduxState => reduxState.entities.orderItemTasks)

  const orderItemsPayload = useOrderItems(
    {
      filters: {
        ...filters,
      },
      requestOptions,
      performHttpRequests: true,
    },
  )

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItems,
    filteredOrderItemsCount,
    hasFilteredOrderItems,
    loading,
  } = orderItemsPayload

  const groupedOrderItems = useMemo(() => {
    const groupedItems = getGroupedOrderItems(filteredOrderItems, orderItemTasks)

    return priorityUserId ? sortByPriorityOrderItems({
      groupedItems,
      priorityOnly: !!priorityForUserId,
      selectedPriorityUserId: priorityUserId,
    }) : groupedItems
  }, [
    campaignsUpdatedAt,
    filterAgencyId,
    servicesUpdatedAt,
    statusesUpdatedAt,
    orderItemsUpdatedAt,
    JSON.stringify(filters),
  ])

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItemsCount,
    groupedOrderItems,
    hasFilteredOrderItems,
    loading,
  }
}

export default useSignboards
